import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Stack, Card, Chip, Skeleton, Alert, alpha, useTheme,
  LinearProgress, Button,
  TextField, IconButton, Divider, InputAdornment,
} from '@mui/material';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from '../components/common/modal';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  Target, TrendingUp, Megaphone, Settings as SettingsIcon,
  Wallet, Percent, Users as UsersIcon, BadgeIndianRupee, ArrowUpRight, ArrowDownRight,
  Lightbulb, AlertTriangle, CheckCircle2, Sparkles, Plus, Trash2, Wand2, Globe,
} from 'lucide-react';
import axios from '../utils/axios';
import DashboardSectionHeader from '../components/DashboardSectionHeader';
import KpiTile from '../components/charts/KpiTile';
import DonutCard from '../components/charts/DonutCard';
import AdSenseRevenueSection from '../components/AdSenseRevenueSection';

const RANGES = [
  { label: '6M', value: 6 },
  { label: '12M', value: 12 },
  { label: '24M', value: 24 },
];

const gridItemSx = { display: 'flex', '& > *': { flex: 1, width: '100%', minWidth: 0 } };

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtShort = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v}`;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: '0 12px 24px rgba(20,26,56,0.16)', border: '1px solid rgba(145,158,171,0.18)', p: 1.5, minWidth: 160 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.75 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{p.name}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>{fmtINR(p.value)}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

const DeltaChip = ({ value, suffix = '%', invert = false }) => {
  const theme = useTheme();
  const positive = value > 0;
  const negative = value < 0;
  const good = invert ? negative : positive;
  const color = value === 0 ? theme.palette.text.disabled : good ? theme.palette.success.main : theme.palette.error.main;
  const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : null;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color }}>
      {Icon && <Icon size={14} />}
      <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
        {positive ? '+' : ''}{value}{suffix}
      </Typography>
    </Stack>
  );
};

const SUGGESTION_STYLE = {
  success: { color: '#16a34a', icon: CheckCircle2 },
  warning: { color: '#dc2626', icon: AlertTriangle },
  opportunity: { color: '#7c3aed', icon: Sparkles },
  info: { color: '#0ea5e9', icon: Lightbulb },
};

const SettingsDialog = ({ open, onClose, settings, suggested, onSaved }) => {
  const theme = useTheme();
  const [form, setForm] = useState(settings || {});
  const [otherCosts, setOtherCosts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(settings || {});
      // Seed the itemised list from saved items, or migrate a legacy single
      // "other cost" figure into one editable row so nothing is lost.
      const items = Array.isArray(settings?.other_costs) ? settings.other_costs : [];
      if (items.length > 0) {
        setOtherCosts(items.map((r) => ({ note: r.note || '', amount: r.amount ?? '' })));
      } else if (Number(settings?.other_cost_monthly) > 0) {
        setOtherCosts([{ note: '', amount: settings.other_cost_monthly }]);
      } else {
        setOtherCosts([]);
      }
      setError('');
    }
  }, [open, settings]);

  const handle = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const fields = [
    { name: 'monthly_target', label: 'Monthly Revenue Target', adorn: '₹' },
    { name: 'payment_gateway_fee_pct', label: 'Payment Gateway Fee', adorn: '%' },
    { name: 'infra_cost_monthly', label: 'Infrastructure / Server Cost (monthly)', adorn: '₹' },
    { name: 'marketing_cost_monthly', label: 'Marketing Spend (monthly)', adorn: '₹' },
    { name: 'team_cost_monthly', label: 'Team / Salary Cost (monthly)', adorn: '₹' },
  ];

  const setItem = (i, key, value) => setOtherCosts((list) => list.map((row, idx) => idx === i ? { ...row, [key]: value } : row));
  const addItem = () => setOtherCosts((list) => [...list, { note: '', amount: '' }]);
  const removeItem = (i) => setOtherCosts((list) => list.filter((_, idx) => idx !== i));

  const othersTotal = otherCosts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        // Drop fully-empty rows; the backend re-validates and syncs the total.
        other_costs: otherCosts
          .map((r) => ({ note: (r.note || '').trim(), amount: Number(r.amount) || 0 }))
          .filter((r) => r.note !== '' || r.amount > 0),
      };
      await axios.put('/admin/revenue/settings', payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminDialog open={open} onClose={!saving ? onClose : undefined} maxWidth="sm">
      <AdminDialogHeader
        title="Revenue Settings"
        subtitle="Target & operating costs power EBITDA and P&L calculations"
        icon={SettingsIcon}
        onClose={!saving ? onClose : undefined}
      />
      <AdminDialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* Suggested target hint inside settings */}
        {suggested?.amount > 0 && (
          <Box
            sx={{
              mb: 2, p: 1.5, borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              display: 'flex', alignItems: 'center', gap: 1.25,
            }}
          >
            <Wand2 size={18} color={theme.palette.primary.main} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                Suggested target: {fmtINR(suggested.amount)}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                Based on your last {suggested.basis}-month trend.
              </Typography>
            </Box>
            <Button size="small" onClick={() => setForm((f) => ({ ...f, monthly_target: suggested.amount }))} sx={{ fontWeight: 700 }}>
              Use
            </Button>
          </Box>
        )}

        <Grid container spacing={2}>
          {fields.map((f) => (
            <Grid size={{ xs: 12, sm: 6 }} key={f.name}>
              <TextField
                fullWidth
                type="number"
                label={f.label}
                name={f.name}
                value={form[f.name] ?? ''}
                onChange={handle}
                InputProps={f.adorn === '₹'
                  ? { startAdornment: <InputAdornment position="start">₹</InputAdornment> }
                  : { endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
          ))}
        </Grid>

        {/* AdSense Monetization Settings */}
        <Divider sx={{ my: 2.5 }} />
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Google AdSense Monetization Settings</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Parameters used for display ad revenue calculation</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Actual AdSense Monthly Payout"
              name="adsense_revenue_monthly"
              value={form.adsense_revenue_monthly ?? ''}
              onChange={handle}
              helperText="Overrides calculation if > 0"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Monthly Page Views"
              name="adsense_page_views"
              value={form.adsense_page_views ?? ''}
              onChange={handle}
              InputProps={{ endAdornment: <InputAdornment position="end">views</InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="AdSense CTR (%)"
              name="adsense_ctr"
              value={form.adsense_ctr ?? ''}
              onChange={handle}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Average CPC"
              name="adsense_cpc"
              value={form.adsense_cpc ?? ''}
              onChange={handle}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
          </Grid>
        </Grid>

        {/* Itemised other operating costs, each with a note */}
        <Divider sx={{ my: 2.5 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Other Operating Costs</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Add each cost with a note so the P&amp;L stays clear.</Typography>
          </Box>
          <Button size="small" startIcon={<Plus size={15} />} onClick={addItem} sx={{ fontWeight: 700 }}>Add</Button>
        </Stack>

        {otherCosts.length === 0 ? (
          <Box sx={{ py: 2, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2, color: 'text.disabled' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>No other costs added. Click “Add” to itemise one.</Typography>
          </Box>
        ) : (
          <Stack spacing={1.25}>
            {otherCosts.map((row, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Note (e.g. Legal & compliance)"
                  value={row.note}
                  onChange={(e) => setItem(i, 'note', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="0"
                  value={row.amount}
                  onChange={(e) => setItem(i, 'amount', e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  sx={{ width: 140 }}
                />
                <IconButton size="small" color="error" onClick={() => removeItem(i)} aria-label="Remove cost">
                  <Trash2 size={16} />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>Total other costs:</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{fmtINR(othersTotal)}</Typography>
            </Stack>
          </Stack>
        )}
      </AdminDialogContent>
      <AdminDialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving} sx={{ fontWeight: 600 }}>Cancel</Button>
        <Button onClick={save} variant="contained" color="primary" disabled={saving} sx={{ px: 3, borderRadius: 2, fontWeight: 700 }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

const RevenueView = () => {
  const theme = useTheme();
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [applyingGoal, setApplyingGoal] = useState(false);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/admin/revenue/overview', { params: { months }, signal });
      setData(res.data);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      setError(err.response?.data?.msg || 'Failed to load revenue data');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const t = data?.target || {};
  const b = data?.breakdown || {};
  const ebitda = data?.ebitda || {};
  const pnl = data?.pnl || {};
  const metrics = data?.metrics || {};

  const progressClamped = Math.min(100, t.progressPct || 0);
  const targetColor = t.onTrack ? theme.palette.success.main : theme.palette.warning.main;

  const suggested = t.suggested;
  const showSuggestion = suggested?.amount > 0;
  const suggestionDiffers = showSuggestion && suggested.amount !== t.amount;

  const applySuggestedTarget = async () => {
    if (!suggested?.amount) return;
    setApplyingGoal(true);
    setError('');
    try {
      await axios.put('/admin/revenue/settings', { monthly_target: suggested.amount });
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to apply suggested target');
    } finally {
      setApplyingGoal(false);
    }
  };

  const kpiTiles = [
    { label: 'Current Revenue', value: fmtShort(data?.currentRevenue), icon: <Wallet size={20} />, color: theme.palette.primary.main, growth: data?.momGrowthPct, secondary: 'this month vs last' },
    { label: 'Promotion Rev.', value: fmtShort(b.promotion), icon: <Megaphone size={20} />, color: '#A855F7', secondary: `${b.promotionSharePct || 0}% of revenue` },
    { label: 'Subscription Rev.', value: fmtShort(b.subscription), icon: <BadgeIndianRupee size={20} />, color: theme.palette.info.main, secondary: `${b.subscriptionSharePct || 0}% of revenue` },
    { label: 'AdSense Rev.', value: fmtShort(data?.adsense?.effectiveMonthly || b.adsense), icon: <Globe size={20} />, color: '#10B981', secondary: b.adsenseIncluded ? `${b.adsenseSharePct || 0}% of revenue` : 'standalone ad stream' },
    { label: 'EBITDA', value: fmtShort(ebitda.ebitda), icon: <TrendingUp size={20} />, color: ebitda.ebitda < 0 ? theme.palette.error.main : theme.palette.success.main, secondary: 'earnings before ITDA' },
    { label: 'EBITDA Margin', value: `${ebitda.ebitdaMarginPct || 0}%`, icon: <Percent size={20} />, color: '#f59e0b', secondary: 'profitability' },
    { label: 'ARPU', value: fmtShort(metrics.arpu), icon: <UsersIcon size={20} />, color: '#0ea5e9', secondary: `${metrics.payingCustomers || 0} paying customers` },
  ];

  const pnlRows = [
    { label: 'Gross Revenue', key: 'revenue' },
    { label: 'Payment Gateway Fees', key: 'gatewayFees', cost: true },
    { label: 'Fixed Operating Costs', key: 'fixedOpEx', cost: true },
    { label: 'Total Operating Expense', key: 'totalOpEx', cost: true, strong: true },
    { label: 'EBITDA', key: 'ebitda', strong: true, highlight: true },
  ];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
      <DashboardSectionHeader
        title="Revenue"
        subtitle="Target pacing, revenue mix, EBITDA and month-over-month P&L — with growth suggestions."
        ranges={RANGES}
        rangeValue={months}
        onRangeChange={setMonths}
        onRefresh={load}
        refreshTooltip="Refresh revenue"
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon size={16} />}
            onClick={() => setSettingsOpen(true)}
            sx={{ borderColor: theme.palette.divider, color: 'text.secondary', fontWeight: 700 }}
          >
            Settings
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {(loading ? Array.from({ length: 7 }) : kpiTiles).map((tile, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 3, lg: 1.714 }} key={i} sx={gridItemSx}>
            {loading
              ? <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3, width: '100%' }} />
              : <KpiTile label={tile.label} value={tile.value} icon={tile.icon} color={tile.color} growth={tile.growth} secondary={tile.secondary} />}
          </Grid>
        ))}
      </Grid>

      {/* Target + Mix */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <Card sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: -40, right: -20, opacity: 0.08, color: targetColor }}>
                <Target size={150} />
              </Box>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha(targetColor, 0.14), color: targetColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Target size={22} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.2 }}>Monthly Target</Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{data?.period?.label}</Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Chip
                  label={t.onTrack ? 'On track' : 'Behind pace'}
                  size="small"
                  icon={t.onTrack ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                  sx={{ fontWeight: 800, bgcolor: alpha(targetColor, 0.14), color: targetColor, '& .MuiChip-icon': { color: targetColor } }}
                />
              </Stack>

              <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, lineHeight: 1 }}>
                  {fmtINR(t.achieved)}
                </Typography>
                <Typography sx={{ fontSize: 15, color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                  / {fmtINR(t.amount)}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={progressClamped}
                sx={{ height: 12, borderRadius: 6, my: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.16), '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: targetColor } }}
              />

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {[
                  { label: 'Achieved', value: `${t.progressPct || 0}%`, color: targetColor },
                  { label: 'Remaining', value: fmtShort(t.remaining), color: theme.palette.text.primary },
                  { label: 'Projected (run-rate)', value: fmtShort(t.projected), color: t.projected >= t.amount ? theme.palette.success.main : theme.palette.warning.main },
                ].map((s) => (
                  <Grid size={4} key={s.label}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: { xs: 15, md: 18 }, fontWeight: 800, color: s.color }}>{s.value}</Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Data-driven goal suggestion */}
              {showSuggestion && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.25}
                  sx={{
                    mt: 2.5, p: 1.5, borderRadius: 2,
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  }}
                >
                  <Box sx={{ color: theme.palette.primary.main, display: 'flex' }}><Wand2 size={18} /></Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                      {t.isSet ? 'Suggested goal' : 'Recommended starting goal'}: {fmtINR(suggested.amount)}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      Based on your last {suggested.basis}-month run-rate
                      {suggested.growthPct > 0 ? ` · trending +${suggested.growthPct}%` : ''}.
                    </Typography>
                  </Box>
                  {suggestionDiffers ? (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={applyingGoal}
                      onClick={applySuggestedTarget}
                      sx={{ fontWeight: 700, flexShrink: 0 }}
                    >
                      {applyingGoal ? 'Applying…' : (t.isSet ? 'Use this goal' : 'Set as target')}
                    </Button>
                  ) : (
                    <Chip size="small" icon={<CheckCircle2 size={13} />} label="On your target"
                      sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.dark, '& .MuiChip-icon': { color: theme.palette.success.main } }} />
                  )}
                </Stack>
              )}
            </Card>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <DonutCard
              title="Revenue Mix"
              subtitle={`${data?.period?.label} · ${fmtINR(data?.currentRevenue)}`}
              data={[
                { name: 'Promotion', count: b.promotion || 0 },
                { name: 'Subscription', count: b.subscription || 0 },
                ...((b.adsenseIncluded || (b.adsense || 0) > 0) ? [{ name: 'AdSense Ads', count: b.adsense || 0 }] : []),
              ]}
              valueKey="count"
              labelKey="name"
              height={240}
            />
          )}
        </Grid>
      </Grid>

      {/* Trend graph */}
      <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Revenue Trend</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Promotion vs subscription vs display ad revenue over the last {months} months</Typography>
          </Box>
        </Stack>
        {loading ? (
          <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
        ) : (
          <Box sx={{ width: '100%', height: 340 }}>
            <ResponsiveContainer>
              <ComposedChart data={data?.trend || []} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.grey[500], 0.2)} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fontWeight: 600 }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={56} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }} />
                <Legend wrapperStyle={{ fontSize: 12, fontWeight: 600 }} iconType="circle" />
                <Bar dataKey="promotion" name="Promotion" stackId="rev" fill="#A855F7" radius={[0, 0, 0, 0]} maxBarSize={44} />
                <Bar dataKey="subscription" name="Subscription" stackId="rev" fill={theme.palette.info.main} radius={[b.adsenseIncluded ? 0 : 6, b.adsenseIncluded ? 0 : 6, 0, 0]} maxBarSize={44} />
                {b.adsenseIncluded && (
                  <Bar dataKey="adsense" name="AdSense Ads" stackId="rev" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={44} />
                )}
                <Line type="monotone" dataKey="total" name="Total" stroke={theme.palette.primary.main} strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Card>

      {/* ── Google AdSense Revenue & Estimation Calculator Section ── */}
      <AdSenseRevenueSection
        adsenseData={data?.adsense}
        onSettingsUpdated={load}
      />

      {/* P&L comparison + EBITDA breakdown */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 7 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={340} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.25 }}>Profit &amp; Loss — Month over Month</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
                {pnl.previousLabel} vs {pnl.currentLabel}
              </Typography>

              <Stack direction="row" sx={{ px: 1, pb: 1 }}>
                <Typography sx={{ flex: 1.6, fontSize: 11, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase' }}>Line item</Typography>
                <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 800, color: 'text.disabled', textAlign: 'right', textTransform: 'uppercase' }}>{pnl.previousLabel}</Typography>
                <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 800, color: 'text.disabled', textAlign: 'right', textTransform: 'uppercase' }}>{pnl.currentLabel}</Typography>
                <Typography sx={{ flex: 0.8, fontSize: 11, fontWeight: 800, color: 'text.disabled', textAlign: 'right', textTransform: 'uppercase' }}>Δ</Typography>
              </Stack>
              <Divider />

              {pnlRows.map((row) => {
                const cur = pnl.current?.[row.key] || 0;
                const prev = pnl.previous?.[row.key] || 0;
                return (
                  <Stack
                    key={row.key}
                    direction="row"
                    alignItems="center"
                    sx={{
                      px: 1, py: 1.25,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      bgcolor: row.highlight ? alpha(theme.palette.success.main, 0.06) : 'transparent',
                      borderRadius: row.highlight ? 1.5 : 0,
                    }}
                  >
                    <Typography sx={{ flex: 1.6, fontSize: 13.5, fontWeight: row.strong ? 800 : 600, color: row.cost ? 'text.secondary' : 'text.primary' }}>
                      {row.cost ? '− ' : ''}{row.label}
                    </Typography>
                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 600, textAlign: 'right', color: 'text.secondary' }}>{fmtShort(prev)}</Typography>
                    <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: row.strong ? 800 : 700, textAlign: 'right', color: row.highlight ? (cur < 0 ? theme.palette.error.main : theme.palette.success.dark) : 'text.primary' }}>{fmtShort(cur)}</Typography>
                    <Box sx={{ flex: 0.8, display: 'flex', justifyContent: 'flex-end' }}>
                      <DeltaChip value={prev > 0 ? Math.round(((cur - prev) / prev) * 100) : (cur > 0 ? 100 : 0)} invert={row.cost} />
                    </Box>
                  </Stack>
                );
              })}

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1, pt: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>EBITDA Margin</Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>{pnl.previous?.ebitdaMarginPct || 0}%</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 800 }}>{pnl.current?.ebitdaMarginPct || 0}%</Typography>
                  <DeltaChip value={pnl.deltas?.ebitdaMarginPct || 0} suffix="pp" />
                </Stack>
              </Stack>
            </Card>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 5 }} sx={gridItemSx}>
          {loading ? (
            <Skeleton variant="rounded" height={340} sx={{ borderRadius: 3, width: '100%' }} />
          ) : (
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.25 }}>EBITDA Breakdown</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>{data?.period?.label} · margin {ebitda.ebitdaMarginPct || 0}%</Typography>

              <Stack spacing={1.25}>
                <BreakdownRow label="Gross Revenue" value={ebitda.revenue} color={theme.palette.primary.main} bold />
                {b.adsenseIncluded && (b.adsense || 0) > 0 && (
                  <BreakdownRow label="↳ AdSense Ads (0% gateway fee)" value={b.adsense} color="#059669" indent />
                )}
                <BreakdownRow label="Payment Gateway Fees" value={-(ebitda.costBreakdown?.gatewayFees || 0)} />
                <BreakdownRow label="Infrastructure" value={-(ebitda.costBreakdown?.infra || 0)} />
                <BreakdownRow label="Marketing" value={-(ebitda.costBreakdown?.marketing || 0)} />
                <BreakdownRow label="Team / Salary" value={-(ebitda.costBreakdown?.team || 0)} />
                {ebitda.costBreakdown?.otherItems?.length > 0 ? (
                  ebitda.costBreakdown.otherItems.map((it, i) => (
                    <BreakdownRow key={i} label={it.note?.trim() ? it.note : 'Other'} value={-(it.amount || 0)} indent />
                  ))
                ) : (
                  <BreakdownRow label="Other" value={-(ebitda.costBreakdown?.other || 0)} />
                )}
                <Divider />
                <BreakdownRow label="EBITDA" value={ebitda.ebitda} color={ebitda.ebitda < 0 ? theme.palette.error.main : theme.palette.success.main} bold big />
              </Stack>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: alpha(ebitda.ebitda < 0 ? theme.palette.error.main : theme.palette.success.main, 0.08) }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>
                  For every {fmtINR(100)} of revenue, you keep{' '}
                  <b style={{ color: ebitda.ebitda < 0 ? theme.palette.error.main : theme.palette.success.dark }}>
                    ₹{Math.round((ebitda.ebitdaMarginPct || 0))}
                  </b>{' '}
                  as operating profit.
                </Typography>
              </Box>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Suggestions */}
      {!loading && data?.suggestions?.length > 0 && (
        <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, mb: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ color: theme.palette.warning.main, display: 'flex' }}><Lightbulb size={20} /></Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Revenue Suggestions</Typography>
          </Stack>
          <Grid container spacing={2}>
            {data.suggestions.map((s, i) => {
              const style = SUGGESTION_STYLE[s.type] || SUGGESTION_STYLE.info;
              const Icon = style.icon;
              return (
                <Grid size={{ xs: 12, md: 6 }} key={i}>
                  <Stack direction="row" spacing={1.5} sx={{ p: 1.75, borderRadius: 2, height: '100%', border: `1px solid ${theme.palette.divider}`, bgcolor: alpha(style.color, 0.04) }}>
                    <Box sx={{ color: style.color, mt: '2px', flexShrink: 0 }}><Icon size={18} /></Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 800, mb: 0.25 }}>{s.title}</Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>{s.message}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Card>
      )}

      {!loading && data && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1, mb: 2, color: 'text.disabled' }}>
          <Sparkles size={13} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>
            Revenue from live paid orders · EBITDA & P&L use your configured cost assumptions (edit in Settings)
          </Typography>
        </Stack>
      )}

      <SettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={data?.settings}
        suggested={suggested}
        onSaved={load}
      />
    </Box>
  );
};

const BreakdownRow = ({ label, value, color, bold, big, indent }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography noWrap sx={{ fontSize: big ? 14.5 : 13, fontWeight: bold ? 800 : 600, color: 'text.secondary', pl: indent ? 1.5 : 0, minWidth: 0, mr: 1 }}>{label}</Typography>
    <Typography sx={{ fontSize: big ? 16 : 13.5, fontWeight: bold ? 800 : 700, color: color || 'text.primary' }}>
      {value < 0 ? `− ₹${Math.abs(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
    </Typography>
  </Stack>
);

export default RevenueView;
